import { defineStore } from "pinia";

import type { UserLegalAgreementState } from "./Models/State";
import type { User } from "@/Modules/Models/Domain/User";
import type { LegalAgreement } from "@/Modules/Models/Domain/LegalAgreement";
import { StateNamespace } from "@/Modules/App/Models/State/StateNamespace";

import getUserLegalAgreementsAH from "./ActionHandlers/GetUserLegalAgreements";
import getHTMLLegalAgreementAH from "./ActionHandlers/GetHTMLLegalAgreement";
import acceptLegalAgreementsAH from "./ActionHandlers/AcceptLegalAgreements";
import getUserDataAH from "@/Modules/User/ActionHandlers/GetUserData";

import useUserStore from "@/Modules/User/Store";
import { hasValue } from "@/Utils/Objects";

const userLegalAgreementState: UserLegalAgreementState = {
  legalAgreements: [],
};

export const useUserLegalAgreementStore = defineStore(StateNamespace.USER_LEGAL_AGREEMENT, {
  state: () => ({ ...userLegalAgreementState }),
  getters: {
    countPendingToSign(state: UserLegalAgreementState): number {
      return state.legalAgreements.filter(
        (la) => la.contractorSignedDate === null && la.contractedSignedDate !== null
      ).length;
    },
    getTotalPendingNotifications(): number {
      const userStore = useUserStore();

      return userStore.getUserIsPatient ? this.countPendingToSign : 0;
    },
    activeLegalAgreements(): LegalAgreement[] {
      return this.legalAgreements.filter(
        (la) => hasValue(la.contractedSignedDate) || hasValue(la.contractorSignedDate)
      );
    },
  },
  actions: {
    async getTherapistInfo(uuid: string): Promise<User | undefined> {
      return getUserDataAH(uuid);
    },
    async fetchLegalAgreements(customerId: string): Promise<void> {
      const userStore = useUserStore();
      const userId = userStore.getUserIsTherapist && userStore.user?.id ? userStore.user.id : undefined;

      await getUserLegalAgreementsAH(customerId, userId);
    },
    async fetchHTMLLegalAgreement(userId: string, contractUuid: string): Promise<string | null> {
      return getHTMLLegalAgreementAH(userId, contractUuid);
    },
    async acceptLegalAgreement(uuid: string) {
      await acceptLegalAgreementsAH(uuid);
    },
  },
});

export type UserLegalAgreementStore = ReturnType<typeof useUserLegalAgreementStore>;

export default useUserLegalAgreementStore;
